import type { NormDoc, NormLog, NormNat, NormRow, NormSt, NormUnit, NormVer } from './normTypes';

/** Ngày "hôm nay" của dữ liệu mẫu board. */
export const NORM_TODAY = '2026-10-22';
/** [mã, tên, màu ô, tên thường trong "Phí hỗ trợ dịch vụ …"]. */
export const NORM_SV: ReadonlyArray<readonly [string, string, string, string]> = [
  ['CCF', 'Hải quan', 'var(--d-blue)', 'hải quan'],
  ['PTF', 'Kiểm dịch', 'var(--d-green)', 'kiểm dịch'],
  ['COO', 'C/O', 'var(--d-violet)', 'C/O'],
  ['FUM', 'Hun trùng', 'var(--d-amber)', 'hun trùng'],
  ['TRF', 'Trucking', 'var(--route)', 'trucking'],
  ['SCY', 'Cảng / depot', 'var(--d-gray)', 'cảng / depot'],
];
export const NORM_PORT: Record<string, string> = { '*': 'Mọi nơi', CLI: 'Cát Lái', CMT: 'Cái Mép', HPH: 'Hiệp Phước', ICD: 'ICD Sóng Thần', DPL: 'Depot Phước Long' };
export const NORM_UNIT: Record<NormUnit, string> = { c20: 'Cont 20', c40: 'Cont 40', lot: 'Lô', trip: 'Chuyến', hour: 'Giờ' };
export const NORM_APR = { bp: ['Trưởng BP', '', 'Vượt ngưỡng → Trưởng bộ phận duyệt'], ktt: ['Trưởng BP', '+ KTT', 'Vượt ngưỡng → Trưởng bộ phận, rồi Kế toán trưởng duyệt'] } as const;
/** [nhãn, màu chữ, nền]. */
export const NORM_DOC: Record<NormDoc, readonly [string, string, string]> = {
  INV: ['Hoá đơn', 'var(--ok)', 'var(--ok-bg)'],
  RCP: ['Biên lai / bảng kê', 'var(--tx2)', 'var(--sub)'],
  NDC: ['Không chứng từ', 'var(--lb)', 'var(--sub)'],
};
export const NORM_NAT: Record<NormNat, readonly [string, string, string]> = {
  OBH: ['Chi hộ', 'var(--tx2)', 'var(--sub)'],
  BIL: ['Giá vốn có bán lại', 'var(--link)', 'var(--pri-bg)'],
  ABS: ['HANA chịu', 'var(--warn)', 'var(--warn-bg)'],
};
export const NORM_RCPT = { ind: 'Cá nhân', biz: 'Doanh nghiệp tư nhân' } as const;
export const NORM_ST: Record<NormSt, readonly [string, string, string]> = {
  on: ['Hiệu lực', 'var(--ok)', 'var(--ok-bg)'],
  wait: ['Chờ duyệt', 'var(--warn)', 'var(--warn-bg)'],
  sched: ['Sắp áp dụng', 'var(--link)', 'var(--pri-bg)'],
  off: ['Hết hiệu lực', 'var(--lb)', 'var(--sub)'],
};

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const dk = (s: string) => {
  if (!s) return '—';
  if (!/^\d{4}-\d\d-\d\d/.test(s)) return s;
  const p = s.split('-');
  return `${(p[2] ?? '').slice(0, 2)}/${MON[+(p[1] ?? 1) - 1]}`;
};
export const dkt = (s: string) => `${dk(s.slice(0, 10))} ${s.slice(11, 16)}`;
export const vnd = (x: number) => String(Math.round(x)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
export const kk = (x: number) => (x >= 1e6 ? (x / 1e6).toFixed(1).replace('.', ',').replace(',0', '') + 'tr' : x ? Math.round(x / 1000) + 'k' : '0');
export const thrText = (p: number, a: number) => (p || a ? `${p ? `${p}%` : ''}${p && a ? ' · ' : ''}${a ? kk(a) : ''}` : 'Không lệch');
const prevDay = (s: string) => new Date(Date.parse(`${s}T00:00:00Z`) - 86400000).toISOString().slice(0, 10);

export interface NormVerView extends NormVer { i: number; v: string }
export interface NormView extends Omit<NormRow, 'vers'> { vers: NormVerView[]; cur: NormVerView; next: NormVerView | null }

/** Điền ngày hết của bản cũ theo bản kế; bản hiện hành = bản `on` mới nhất đã tới ngày; bản kế = chờ duyệt / sắp áp dụng sau nó. */
export function resolveNorms(rows: NormRow[]): NormView[] {
  return rows.map((n) => {
    const vers: NormVerView[] = n.vers.map((x, i) => ({ ...x, i, v: `v${i + 1}` }));
    vers.forEach((x, i) => {
      const nx = vers.slice(i + 1).find((w) => w.st === 'on' || w.st === 'sched');
      if (nx && !x.to && (x.st === 'on' || x.st === 'off')) x.to = prevDay(nx.from);
    });
    const cur = vers.filter((x) => x.st === 'on' && x.from <= NORM_TODAY).slice(-1)[0] ?? vers[vers.length - 1]!;
    const next = vers.find((x) => x.i > cur.i && (x.st === 'wait' || x.st === 'sched')) ?? null;
    return { ...n, vers, cur, next };
  });
}

export const inPort = (n: Pick<NormRow, 'port'>, pf: string) => pf === 'all' || n.port === pf || n.port === '*';
export const svOf = (k: string) => NORM_SV.find((x) => x[0] === k) ?? NORM_SV[0]!;
export const countOf = (rows: NormView[], sv: string, pf: string, grp?: NormRow['grp']) => rows.filter((n) => n.sv === sv && inPort(n, pf) && (!grp || n.grp === grp)).length;
export const waitOf = (rows: NormView[], sv: string) => rows.filter((n) => n.sv === sv && n.vers.some((v) => v.st === 'wait')).length;

/** Dòng hiển thị: `all` = mọi phiên bản (bản cũ mờ), không thì chỉ bản hiện hành. */
export function normRows(rows: NormView[], sv: string, pf: string, grp: NormRow['grp'], all: boolean) {
  return rows
    .filter((n) => n.sv === sv && n.grp === grp && inPort(n, pf))
    .flatMap((n) => (all ? n.vers.slice().reverse().map((x) => ({ n, x, old: x.i !== n.cur.i })) : [{ n, x: n.cur, old: false }]));
}

const hs = (s: string) => [...s].reduce((a, c) => (a * 31 + c.charCodeAt(0)) % 100003, 7);
const fixedFee = (n: Pick<NormRow, 'nat' | 'docs'>) => n.nat === 'OBH' && n.docs.length === 1 && n.docs[0] === 'RCP';
/** Số liệu thực tế 90 ngày (mô phỏng cố định theo mã dòng — dữ liệu mẫu). `lim` = định mức + ngưỡng nhỏ hơn của % và ₫. */
export function normStats(n: NormView) {
  let seed = hs(n.id);
  const rnd = () => ((seed = (seed * 9301 + 49297) % 233280) / 233280);
  const v = n.cur;
  const N = 6 + Math.floor(rnd() * 58);
  const skew = n.id === 'n02' || n.id === 'n24' || n.id === 'n31' ? 0.2 : 0.06;
  const xs: number[] = [];
  const wk: number[][] = Array.from({ length: 12 }, () => []);
  for (let i = 0; i < N; i++) {
    const a = fixedFee(n) ? v.amt : Math.round((v.amt * (0.86 + rnd() * 0.22 + (rnd() < 0.3 ? skew : 0))) / 5000) * 5000;
    xs.push(a);
    wk[Math.floor(rnd() * 12)]!.push(a);
  }
  const s = xs.slice().sort((a, b) => a - b);
  const avg = xs.reduce((a, b) => a + b, 0) / Math.max(1, N);
  const p90 = s[Math.floor(0.9 * (N - 1))] ?? 0;
  const isOver = (a: number) => a - v.amt > 0 && (a - v.amt > (v.amt * v.thrP) / 100 || (v.thrA > 0 && a - v.amt > v.thrA) || (!v.thrP && !v.thrA));
  const over = xs.filter(isOver).length;
  const wavg = wk.map((L) => (L.length ? L.reduce((a, b) => a + b, 0) / L.length : 0));
  return { N, avg, p90, over, wavg, lim: v.amt + Math.min(v.thrP ? (v.amt * v.thrP) / 100 : Infinity, v.thrA || Infinity) };
}

/** Lịch sử của một dòng (mới nhất trước); chưa có thì suy ra "Tạo v1". */
export function logOf(log: NormLog[], n: NormView) {
  const L = log.filter((l) => l.row === n.id).sort((a, b) => (a.at < b.at ? 1 : -1));
  return L.length ? L : [{ row: n.id, who: 'HO•••TN', at: `${n.vers[0]!.from} 09:00`, what: 'Tạo v1', old: '—', now: vnd(n.vers[0]!.amt) }];
}
